import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useRouter } from '@tanstack/react-router';
import { useMemo, useState, type FormEvent } from 'react';
import { Field } from '@/components/field';
import { EnvironmentBadge } from '@/components/environment-badge';
import { LanguageSwitch } from '@/components/language-switch';
import { Logo } from '@/components/logo';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, errorMessage, setToken, ApiError } from '@/lib/api';
import { browserCountry, countries } from '@/lib/countries';
import { locale, t } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';

/**
 * Self-registration (docs/features/accounts-and-workspaces.md): a new user and an Account named after the
 * business, then signed in — the next step is the first workspace.
 */
export function RegisterPage() {
  const router = useRouter();
  const queryClient = useQueryClient();
  const health = useQuery({ queryKey: ['health'], queryFn: () => api.health(), staleTime: Infinity, retry: false });
  const countryList = useMemo(() => countries(), []);
  const [form, setForm] = useState({ name: '', email: '', password: '', business: '', country: browserCountry() ?? '', phone: '' });
  const [error, setError] = useState<{ message: string; signIn?: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (field: keyof typeof form) => (value: string) => setForm((f) => ({ ...f, [field]: value }));

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (!form.country) return setError({ message: t('Choose your country.') });
    setBusy(true);
    try {
      const res = await api.auth.register({ ...form, email: form.email.trim(), locale });
      queryClient.clear();
      setToken(res.token);
      await router.navigate({ to: '/' });
      void queryClient.invalidateQueries({ queryKey: qk.me });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) setError({ message: errorMessage(err), signIn: true });
      else if (err instanceof ApiError && err.status === 404) setError({ message: t('Registration is closed on this installation.') });
      else setError({ message: errorMessage(err) });
    } finally {
      setBusy(false);
    }
  }

  const closed = health.data?.signup === false;

  return (
    <div className="relative flex min-h-full items-center justify-center bg-muted/40 px-4 py-12">
      <div className="absolute top-3 right-3">
        <LanguageSwitch />
      </div>
      <div className="w-full max-w-md">
        <div className="mb-8 flex flex-col items-center gap-3 text-center">
          <Logo className="size-10" />
          <EnvironmentBadge />
          <div>
            <h1 className="text-xl font-semibold tracking-tight">{t('Create your Deskworks account')}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{t('Your business gets its own account. You can invite your team later.')}</p>
          </div>
        </div>
        <Card>
          <CardContent className="pt-6">
            {closed ? (
              <p className="text-sm text-muted-foreground">{t('Registration is closed on this installation. Ask an admin to invite you.')}</p>
            ) : (
              <form onSubmit={onSubmit} className="grid gap-4">
                <Field label={t('Your name')}>
                  <Input autoComplete="name" autoFocus required maxLength={120} value={form.name} onChange={(e) => set('name')(e.target.value)} />
                </Field>
                <Field label={t('Business')}>
                  <Input
                    autoComplete="organization"
                    required
                    maxLength={120}
                    value={form.business}
                    onChange={(e) => set('business')(e.target.value)}
                    placeholder={t('Company name')}
                  />
                </Field>
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label={t('Country')}>
                    <Select value={form.country} onValueChange={set('country')}>
                      <SelectTrigger>
                        <SelectValue placeholder={t('Choose…')} />
                      </SelectTrigger>
                      <SelectContent>
                        {countryList.map((c) => (
                          <SelectItem key={c.code} value={c.code}>
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label={t('Phone')}>
                    <Input
                      type="tel"
                      autoComplete="tel"
                      required
                      value={form.phone}
                      onChange={(e) => set('phone')(e.target.value)}
                      placeholder="+49 30 1234567"
                    />
                  </Field>
                </div>
                <Field label={t('Email')}>
                  <Input
                    type="email"
                    autoComplete="email"
                    required
                    value={form.email}
                    onChange={(e) => set('email')(e.target.value)}
                    placeholder={t('you@company.com')}
                  />
                </Field>
                <Field label={t('Password')} hint={t('At least 12 characters.')}>
                  <Input
                    type="password"
                    autoComplete="new-password"
                    required
                    minLength={12}
                    value={form.password}
                    onChange={(e) => set('password')(e.target.value)}
                  />
                </Field>
                {error && (
                  <p role="alert" className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive">
                    {error.message}
                    {error.signIn && (
                      <>
                        {' '}
                        <Link to="/login" className="font-medium underline">
                          {t('Sign in')}
                        </Link>
                      </>
                    )}
                  </p>
                )}
                <Button type="submit" loading={busy} className="w-full">
                  {t('Create account')}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
        <p className="mt-4 text-center text-sm text-muted-foreground">
          {t('Already have an account?')}{' '}
          <Link to="/login" className="font-medium text-foreground hover:underline">
            {t('Sign in')}
          </Link>
        </p>
        <p className="mt-2 text-center text-xs text-muted-foreground">
          <a href="https://deskworks.ai" className="hover:text-foreground">
            {t('← What is Deskworks?')}
          </a>
        </p>
      </div>
    </div>
  );
}
